import { z } from 'zod';

/**
 * AWS Polly Text-to-Speech Configuration
 * @see https://docs.aws.amazon.com/polly/latest/dg/API_SynthesizeSpeech.html
 */
export const PollyTTSConfigSchema = z.object({
  type: z.literal('polly'),
  settings: z.object({
    /**
     * Voice ID to use for speech synthesis
     * Examples: 'Joanna', 'Amy', 'Matthew', 'Salli'
     * @see https://docs.aws.amazon.com/polly/latest/dg/voicelist.html
     */
    voiceId: z.string(),

    /**
     * Engine to use for synthesis
     * - 'neural': High quality, natural sounding (recommended)
     * - 'generative': New generative AI voices
     * - 'standard': Basic TTS
     */
    engine: z.enum(['neural', 'generative', 'standard']),

    /**
     * Audio format for the output.
     * Must be 'pcm' when used with HeyGen's repeatAudio() — the SDK's
     * splitPcm24kStringToChunks() expects raw PCM at 24 kHz / 16-bit / mono.
     * For PCM, the provider requests SampleRate='16000' from Polly (Polly rejects
     * 24000 Hz with InvalidSampleRateException) and then resamples to 24 kHz
     * server-side via linear interpolation before returning the audio buffer.
     */
    outputFormat: z.enum(['mp3', 'ogg_vorbis', 'pcm']).default('pcm'),

    /**
     * Type of input text
     * - 'text': Plain text
     * - 'ssml': Speech Synthesis Markup Language for advanced control
     */
    textType: z.enum(['text', 'ssml']).default('text'),

    /**
     * Language code (optional)
     * Examples: 'en-US', 'en-GB', 'es-ES'
     */
    languageCode: z.string().optional(),

    /**
     * Speech rate adjustment (optional)
     * Range: 0.5 to 2.0 (1.0 is normal speed)
     */
    rate: z.number().min(0.5).max(2.0).optional(),

    /**
     * Pitch adjustment (optional)
     * Range: -10 to 10 (0 is normal pitch)
     */
    pitch: z.number().min(-10).max(10).optional(),
  }),
});

/**
 * OpenAI Text-to-Speech Configuration
 * Based on production usage in chat-api with Azure OpenAI
 * @see https://platform.openai.com/docs/guides/text-to-speech
 */
export const OpenAITTSConfigSchema = z.object({
  type: z.literal('openai'),
  settings: z.object({
    /**
     * TTS model to use
     * - 'tts-1': Standard quality, faster
     * - 'tts-1-hd': High definition, more natural
     */
    model: z.enum(['tts-1', 'tts-1-hd']),

    /**
     * Voice to use for speech synthesis
     * Available voices: alloy, echo, fable, onyx, nova, shimmer
     */
    voice: z.enum(['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']),

    /**
     * Speech speed adjustment (optional)
     * Range: 0.25 to 4.0 (1.0 is normal speed)
     */
    speed: z.number().min(0.25).max(4.0).optional(),

    /**
     * Audio format for the output (optional)
     * Defaults to mp3 if not specified
     */
    responseFormat: z.enum(['mp3', 'opus', 'aac', 'flac']).optional(),
  }),
});

/**
 * Discriminated union of all supported TTS provider configurations
 * This allows type-safe handling of different providers with different settings
 *
 * Usage example:
 * ```typescript
 * const config: TTSConfig = {
 *   type: 'polly',
 *   settings: {
 *     voiceId: 'Joanna',
 *     engine: 'neural',
 *     outputFormat: 'mp3',
 *     textType: 'text'
 *   }
 * };
 * ```
 */
export const TTSConfigSchema = z.discriminatedUnion('type', [PollyTTSConfigSchema, OpenAITTSConfigSchema]);

/**
 * TypeScript type inferred from Polly config schema
 */
export type PollyTTSConfig = z.infer<typeof PollyTTSConfigSchema>;

/**
 * TypeScript type inferred from OpenAI config schema
 */
export type OpenAITTSConfig = z.infer<typeof OpenAITTSConfigSchema>;

/**
 * TypeScript type for any supported TTS configuration
 * This is a discriminated union that provides type narrowing based on the 'type' field
 */
export type TTSConfig = z.infer<typeof TTSConfigSchema>;

/**
 * Type guard to check if a config is for Polly
 */
export function isPollyConfig(config: TTSConfig): config is PollyTTSConfig {
  return config.type === 'polly';
}

/**
 * Type guard to check if a config is for OpenAI
 */
export function isOpenAIConfig(config: TTSConfig): config is OpenAITTSConfig {
  return config.type === 'openai';
}

export const TTSProvider = z.enum(['polly' /*, 'openai'*/]);
export type TTSProvider = z.infer<typeof TTSProvider>;

export const TTSProviderLabels: Record<TTSProvider, string> = {
  polly: 'AWS Polly',
  // openai: 'OpenAI',
};

export const PollyVoices = z.enum([
  'Joanna',
  'Salli',
  'Kendra',
  'Kimberly',
  'Matthew',
  'Amy',
  'Emma',
  'Brian',
]);
export type PollyVoices = z.infer<typeof PollyVoices>;

export const PollyVoiceLabels: Record<PollyVoices, string> = {
  Joanna: 'Joanna - US English, Female',
  Salli: 'Salli - US English, Female',
  Kendra: 'Kendra - US English, Female',
  Kimberly: 'Kimberly - US English, Female',
  Matthew: 'Matthew - US English, Male',
  Amy: 'Amy - British English, Female',
  Emma: 'Emma - British English, Female',
  Brian: 'Brian - British English, Male',
};

export const OpenAIVoices = OpenAITTSConfigSchema.shape.settings.shape.voice;
export type OpenAIVoices = z.infer<typeof OpenAIVoices>;

export const TTSVoicesByProvider: Record<TTSProvider, string[]> = {
  polly: PollyVoices.options,
  // openai: OpenAIVoices.options,
};

export function getTTSProviderVoiceLabel(provider: TTSProvider, voiceId: string): string {
  switch (provider) {
    case 'polly':
      return PollyVoiceLabels[voiceId as PollyVoices] ?? voiceId;
    // case 'openai':
    //   return voiceId;
    default:
      return voiceId;
  }
}

/**
 * Simplified TTS config stored as a feature setting.
 * Only exposes provider + voiceId; full provider-specific defaults are
 * applied by {@link toTTSConfig}.
 */
export const TTSFeatureConfigSchema = z.object({
  type: TTSProvider,
  voiceId: z.string(),
});
export type TTSFeatureConfig = z.infer<typeof TTSFeatureConfigSchema>;

/**
 * Expand a simplified feature config into a full {@link TTSConfig}
 * with sensible provider-specific defaults.
 */
export function toTTSConfig(config: TTSFeatureConfig): TTSConfig {
  switch (config.type) {
    case 'polly':
      return {
        type: 'polly',
        settings: {
          voiceId: config.voiceId,
          engine: 'neural',
          outputFormat: 'pcm',
          textType: 'text',
        },
      };
    // case 'openai':
    //   return {
    //     type: 'openai',
    //     settings: {
    //       model: 'tts-1',
    //       voice: config.voiceId as OpenAITTSConfig['settings']['voice'],
    //     },
    //   };
  }
}
